import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, Stack, Chip, MenuItem, TextField, Button,
  CircularProgress, Grid, InputAdornment, Tabs, Tab, Badge,
  Tooltip, IconButton, Divider, Avatar,
} from '@mui/material'
import {
  Send, PowerSettingsNew, Lock, LockOpen, Block, CheckCircle,
  RestartAlt, Search, FilterList, Refresh, AccessTime,
  DirectionsCar, WarningAmber,
} from '@mui/icons-material'
import { formatDistanceToNow, format } from 'date-fns'
import { useDevices, Device } from '../../hooks/useDevices'
import { useDeviceCommands, useIssueCommand, DeviceCommand } from '../../hooks/useFleet'
import { ConfirmDialog } from '../../components/ConfirmDialog'

// COMMANDS and COMMAND_MAP are built inside the component so labels/descs are translated at render time

const STATUS_CONFIG_BASE: Record<string, { bg: string; color: string; border: string }> = {
  queued:       { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  sent:         { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe' },
  acknowledged: { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0' },
  failed:       { bg: '#fff1f2', color: '#ef4444', border: '#fecaca' },
}
const STATUS_KEYS = Object.keys(STATUS_CONFIG_BASE)

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

export default function CommandsPage() {
  const { t } = useTranslation()

  const COMMANDS = [
    { value: 'lock',           label: t('commands.lock'),          icon: <Lock sx={{ fontSize: 16 }} />,        color: '#f59e0b', desc: t('commands.lockDesc') },
    { value: 'unlock',         label: t('commands.unlock'),        icon: <LockOpen sx={{ fontSize: 16 }} />,    color: '#10b981', desc: t('commands.unlockDesc') },
    { value: 'engine_disable', label: t('commands.engineDisable'), icon: <Block sx={{ fontSize: 16 }} />,       color: '#ef4444', desc: t('commands.engineDisableDesc') },
    { value: 'engine_enable',  label: t('commands.engineEnable'),  icon: <CheckCircle sx={{ fontSize: 16 }} />, color: '#3b82f6', desc: t('commands.engineEnableDesc') },
    { value: 'reboot',         label: t('commands.reboot'),        icon: <RestartAlt sx={{ fontSize: 16 }} />,  color: '#34A8E0', desc: t('commands.rebootDesc') },
  ]

  const STATUS_CONFIG: Record<string, { bg: string; color: string; border: string; label: string }> = {
    queued:       { ...STATUS_CONFIG_BASE.queued,       label: t('commands.queued') },
    sent:         { ...STATUS_CONFIG_BASE.sent,         label: t('commands.sent') },
    acknowledged: { ...STATUS_CONFIG_BASE.acknowledged, label: t('commands.acknowledged') },
    failed:       { ...STATUS_CONFIG_BASE.failed,       label: t('commands.failed') },
  }

  const COMMAND_MAP = Object.fromEntries(COMMANDS.map((c) => [c.value, c]))

  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)
  const { data: commands = [], isLoading, refetch } = useDeviceCommands()
  const issue = useIssueCommand()

  const [deviceId, setDeviceId] = useState('')
  const [command, setCommand] = useState('lock')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(0) // tab index
  const [confirmOpen, setConfirmOpen] = useState(false)

  const deviceMap = useMemo(() => new Map(devices.map((d) => [d.id, d])), [devices])

  const selectedCmd = COMMANDS.find((c) => c.value === command)
  const selectedDevice = devices.find((d) => d.id === deviceId)

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: commands.length }
    STATUS_KEYS.forEach((s) => { c[s] = commands.filter((cmd: DeviceCommand) => cmd.status === s).length })
    return c
  }, [commands])

  const filtered = useMemo(() => {
    let cmds = commands as DeviceCommand[]
    const status = statusFilter === 0 ? 'all' : STATUS_KEYS[statusFilter - 1]
    if (status !== 'all') cmds = cmds.filter((c) => c.status === status)
    if (search.trim()) {
      const q = search.toLowerCase()
      cmds = cmds.filter((c) =>
        c.command.toLowerCase().includes(q) ||
        c.device_id.toLowerCase().includes(q) ||
        (deviceMap.get(c.device_id)?.name ?? '').toLowerCase().includes(q)
      )
    }
    return cmds
  }, [commands, search, statusFilter, deviceMap])

  const stats = useMemo(() => ({
    total: commands.length,
    success: (commands as DeviceCommand[]).filter((c) => c.status === 'acknowledged').length,
    failed: (commands as DeviceCommand[]).filter((c) => c.status === 'failed').length,
    pending: (commands as DeviceCommand[]).filter((c) => c.status === 'queued' || c.status === 'sent').length,
  }), [commands])

  const handleSend = () => {
    if (!deviceId) return
    // Dangerous commands require confirmation
    if (command === 'engine_disable') {
      setConfirmOpen(true)
      return
    }
    issue.mutateAsync({ deviceId, command })
    analytics.track(EVENTS.COMMAND_SENT, { device_id: deviceId, command })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('commands.title')}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
            {t('commands.subtitle')}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()} sx={{ color: '#94a3b8' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('commands.totalCommands'), value: stats.total, color: '#34A8E0', bg: '#EBF6FD', icon: <PowerSettingsNew /> },
          { label: t('commands.queued'), value: stats.pending, color: '#f59e0b', bg: '#fffbeb', icon: <AccessTime /> },
          { label: t('commands.acknowledged'), value: stats.success, color: '#10b981', bg: '#f0fdf4', icon: <CheckCircle /> },
          { label: t('commands.failed'), value: stats.failed, color: '#ef4444', bg: '#fff1f2', icon: <WarningAmber /> },
        ].map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Card sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#0f172a">{kpi.value}</Typography>
                <Typography variant="caption" color="#94a3b8" fontWeight={600} sx={{ fontSize: 11 }}>{kpi.label}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Command Issue Card */}
      <Card sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: 2, background: 'linear-gradient(135deg, #faf5ff 0%, #f8fafc 100%)' }}>
        <Typography variant="subtitle2" fontWeight={700} color="#334155" sx={{ mb: 2 }}>
          {t('fleet.issueCommand')}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
          <TextField select size="small" label={t('fleet.selectDevice')} value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)} sx={{ minWidth: 260 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="" disabled>{t('fleet.selectDevice')}...</MenuItem>
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCar sx={{ fontSize: 14, color: '#94a3b8' }} />
                  {d.name}
                  <Chip label={d.status} size="small" sx={{ ml: 'auto', fontSize: 9, height: 18 }} />
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField select size="small" label={t('fleet.commandType')} value={command}
            onChange={(e) => setCommand(e.target.value)} sx={{ minWidth: 240 }}>
            {COMMANDS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: c.color }}>{c.icon}</Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{c.label}</Typography>
                    <Typography variant="caption" color="#94a3b8" sx={{ fontSize: 10 }}>{c.desc}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Button variant="contained" startIcon={<Send />} onClick={handleSend}
            disabled={!deviceId || issue.isPending}
            sx={{
              background: selectedCmd?.value === 'engine_disable'
                ? 'linear-gradient(135deg,#ef4444,#dc2626)'
                : 'linear-gradient(135deg,#34A8E0,#2B8BB9)',
              borderRadius: 1.5, fontWeight: 600, px: 3, py: 1,
              minWidth: 140,
            }}>
            {issue.isPending ? t('common.loading') : t('commands.sent')}
          </Button>
        </Stack>
        {selectedDevice && selectedCmd && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="#64748b">
              Will send <strong style={{ color: selectedCmd.color }}>{selectedCmd.label}</strong> to <strong>{selectedDevice.name}</strong>
            </Typography>
            {command === 'engine_disable' && (
              <Chip icon={<WarningAmber sx={{ fontSize: 12 }} />} label={t('commands.confirmDangerous')} size="small"
                sx={{ bgcolor: '#fff1f2', color: '#ef4444', border: '1px solid #fecaca', fontSize: 10, height: 20 }} />
            )}
          </Box>
        )}
      </Card>

      {/* Status Tabs */}
      <Card sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Tabs value={statusFilter} onChange={(_, v) => setStatusFilter(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 },
            '& .Mui-selected': { color: '#34A8E0' },
            '& .MuiTabs-indicator': { bgcolor: '#34A8E0' },
          }}>
          <Tab label={<Badge badgeContent={counts.all} color="default" max={999} sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 18, minWidth: 18 } }}>{t('common.all')} &nbsp;&nbsp;</Badge>} />
          {STATUS_KEYS.map((s) => {
            const cfg = STATUS_CONFIG[s]
            return (
              <Tab key={s} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {cfg.label}
                  <Chip label={counts[s] || 0} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }} />
                </Box>
              } />
            )
          })}
        </Tabs>
      </Card>

      {/* Search */}
      <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField size="small" placeholder={t('common.search') + '...'} value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="#94a3b8" fontWeight={600}>
            {filtered.length} of {commands.length} commands
          </Typography>
        </Stack>
      </Card>

      {/* Command History */}
      <Stack spacing={1}>
        {filtered.length === 0 && (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <PowerSettingsNew sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body1" fontWeight={600} color="#64748b">
              {commands.length === 0 ? t('commands.noCommands') : t('common.noData')}
            </Typography>
            <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
              {commands.length === 0 ? t('commands.subtitle') : t('common.search')}
            </Typography>
          </Card>
        )}
        {filtered.map((c: DeviceCommand) => {
          const s = STATUS_CONFIG[c.status] ?? STATUS_CONFIG.queued
          const cmd = COMMAND_MAP[c.command]
          const device = deviceMap.get(c.device_id)
          return (
            <Card key={c.id} sx={{
              p: 2, border: '1px solid #e2e8f0', borderRadius: 2,
              borderLeft: `4px solid ${s.color}`,
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: cmd?.color ? `${cmd.color}15` : '#f1f5f9', color: cmd?.color ?? '#64748b' }}>
                    {cmd?.icon ?? <PowerSettingsNew sx={{ fontSize: 18 }} />}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="#1e293b">
                        {cmd?.label ?? c.command.replace(/_/g, ' ')}
                      </Typography>
                      <Chip label={s.label} size="small" sx={{
                        bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
                        fontSize: 10, fontWeight: 600, height: 20,
                      }} />
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.25 }}>
                      <Typography variant="caption" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DirectionsCar sx={{ fontSize: 11 }} />
                        {device?.name ?? c.device_id.slice(0, 8) + '...'}
                      </Typography>
                      <Typography variant="caption" color="#cbd5e1" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <AccessTime sx={{ fontSize: 11 }} />
                        {formatDistanceToNow(new Date(c.issued_at), { addSuffix: true })}
                      </Typography>
                      {c.completed_at && (
                        <Typography variant="caption" color="#94a3b8">
                          {t('trips.completed')}: {format(new Date(c.completed_at), 'HH:mm:ss')}
                        </Typography>
                      )}
                    </Stack>
                    {c.error && (
                      <Typography variant="caption" color="#ef4444" sx={{ mt: 0.5, display: 'block' }}>
                        Error: {c.error}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="#cbd5e1" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                  {c.id.slice(0, 8)}
                </Typography>
              </Box>
            </Card>
          )
        })}
      </Stack>

      {/* Engine Disable Confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        title={t('commands.engineDisable')}
        message={`${t('commands.confirmDangerous')} ${selectedDevice?.name ?? ''}?`}
        confirmLabel={t('commands.engineDisable')}
        confirmColor="error"
        onConfirm={() => {
          issue.mutateAsync({ deviceId, command: 'engine_disable' })
          analytics.track(EVENTS.COMMAND_SENT, { device_id: deviceId, command: 'engine_disable' })
          setConfirmOpen(false)
        }}
        onClose={() => setConfirmOpen(false)}
        loading={issue.isPending}
      />
    </Box>
  )
}
